import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  directories: [
    {
      name: "My Files",
      files: [
        {
          name: "file1.txt",
          size: "1 KB",
          modified: "2024-12-24",
          type: "file",
        },
        {
          name: "file2.txt",
          size: "2 KB",
          modified: "2024-12-24",
          type: "file",
        },
      ],
      isFolder: true,
      subdirectories: [
        {
          name: "Subfolder 1",
          files: [
            {
              name: "subfile1.txt",
              size: "3 KB",
              modified: "2024-12-24",
              type: "file",
            },
          ],
          isFolder: true,
          subdirectories: [],
        },
        {
          name: "Subfolder 2",
          files: [
            {
              name: "subfile2.txt",
              size: "4 KB",
              modified: "2024-12-24",
              type: "file",
            },
          ],
          isFolder: true,
          subdirectories: [],
        },
      ],
    },
    {
      name: "Presentations",
      files: [
        {
          name: "presentation1.pptx",
          size: "10 KB",
          modified: "2024-12-24",
          type: "file",
        },
      ],
      isFolder: true,
      subdirectories: [],
    },
    {
      name: "Web Project",
      files: [
        {
          name: "index.html",
          size: "2 KB",
          modified: "2024-12-24",
          type: "file",
        },
      ],
      isFolder: true,
      subdirectories: [],
    },
  ],
  categories: {
    "CV Upload": {
      instructions: `
    - Upload your CV following the guidelines.
    - Ensure it is in PDF format.
    - Maximum file size: 2 MB.
    - Keep the file name as FirstName_LastName.pdf.
        `,
      resources: [
        {
          name: "Sample CV 1",
          size: "120 KB",
          type: "PDF",
          uploadedAt: "2024-12-20 10:00 AM",
        },
        {
          name: "Sample CV 2",
          size: "150 KB",
          type: "PDF",
          uploadedAt: "2024-12-20 11:30 AM",
        },
      ],
    },
    Shortlisting: {
      instructions: `
    - Use these resources to shortlist colleges.
    - Evaluate based on courses, location, and fees.
        `,
      resources: [
        {
          name: "Shortlisting Guide.pdf",
          size: "1.5 MB",
          type: "PDF",
          uploadedAt: "2024-12-22 02:15 PM",
        },
        {
          name: "College Comparison.xlsx",
          size: "800 KB",
          type: "Excel",
          uploadedAt: "2024-12-22 03:45 PM",
        },
      ],
    },
    SOP: {
      instructions: `
    - Follow the SOP writing tips provided.
    - Ensure it aligns with the university's requirements.
        `,
      resources: [
        {
          name: "SOP Template.docx",
          size: "30 KB",
          type: "Word Document",
          uploadedAt: "2024-12-21 01:10 PM",
        },
        {
          name: "SOP Writing Guide.pdf",
          size: "500 KB",
          type: "PDF",
          uploadedAt: "2024-12-21 04:30 PM",
        },
      ],
    },
    LOR: {
      instructions: `
    - Use the sample LOR templates.
    - Customize them based on the referee's details.
        `,
      resources: [
        {
          name: "LOR Template 1.docx",
          size: "45 KB",
          type: "Word Document",
          uploadedAt: "2024-12-20 09:00 AM",
        },
        {
          name: "LOR Template 2.docx",
          size: "50 KB",
          type: "Word Document",
          uploadedAt: "2024-12-20 09:30 AM",
        },
      ],
    },
    "University Specific SOP": {
      instructions: `
    - Refer to the sample SOPs for specific universities.
    - Customize as per the university's format.
        `,
      resources: [
        {
          name: "UniversityA_SOP_Sample.pdf",
          size: "600 KB",
          type: "PDF",
          uploadedAt: "2024-12-23 08:15 AM",
        },
        {
          name: "UniversityB_SOP_Sample.pdf",
          size: "700 KB",
          type: "PDF",
          uploadedAt: "2024-12-23 10:45 AM",
        },
      ],
    },
    "Final Documents Upload": {
      instructions: `
    - Upload all the final required documents.
    - Ensure files are in the specified format and size.
        `,
      resources: [
        {
          name: "FinalDocumentChecklist.pdf",
          size: "250 KB",
          type: "PDF",
          uploadedAt: "2024-12-24 07:00 AM",
        },
      ],
    },
    "University Application Process": {
      instructions: `
    - Follow the guidelines for applying to universities.
    - Use the provided checklists and tips.
        `,
      resources: [
        {
          name: "Application Guide.pdf",
          size: "1 MB",
          type: "PDF",
          uploadedAt: "2024-12-19 12:00 PM",
        },
      ],
    },
    "Visa Process": {
      instructions: `
    - Refer to the visa process guide.
    - Prepare the required documents in advance.
        `,
      resources: [
        {
          name: "Visa Checklist.pdf",
          size: "1.2 MB",
          type: "PDF",
          uploadedAt: "2024-12-18 05:00 PM",
        },
        {
          name: "Visa Application Form.pdf",
          size: "300 KB",
          type: "PDF",
          uploadedAt: "2024-12-18 06:30 PM",
        },
      ],
    },
  },

  selectedDirectory: "",
};

const resourcesSlice = createSlice({
  name: "resources",
  initialState,
  reducers: {
    setDirectories: (state, action) => {
      state.directories = action.payload;
    },
    selectDirectory: (state, action) => {
      state.selectedDirectory = action.payload;
    },
    addDirectory: (state, action) => {
      state.directories.push({
        name: action.payload,
        files: [],
        isFolder: true,
        subdirectories: [],
      });
    },
    addFile: (state, action) => {
      const { directoryName, file } = action.payload;
      const directory = state.directories.find(
        (dir) => dir.name === directoryName
      );
      if (directory) {
        directory.files.push(file);
      }
    },
    uploadFile: (state, action) => {
      const { directoryName, file } = action.payload;
      const directory = state.directories.find(
        (dir) => dir.name === directoryName
      );
      if (directory) {
        directory.files.push(file);
      }
    },
    editFile: (state, action) => {
      const { directoryName, oldFileName, newFileName } = action.payload;
      const directory = state.directories.find(
        (dir) => dir.name === directoryName
      );
      if (directory) {
        const file = directory.files.find((file) => file.name === oldFileName);
        if (file) {
          file.name = newFileName;
        }
      }
    },
    deleteFile: (state, action) => {
      const { directoryName, fileName } = action.payload;
      const directory = state.directories.find(
        (dir) => dir.name === directoryName
      );
      if (directory) {
        directory.files = directory.files.filter(
          (file) => file.name !== fileName
        );
      }
    },
    deleteDirectory: (state, action) => {
      const { directoryName } = action.payload;
      state.directories = state.directories.filter(
        (dir) => dir.name !== directoryName
      );
    },
    setCategoryContent: (state, action) => {
      const { category, instructions, resources } = action.payload;

      state.categories = {
        ...state.categories,
        [category]: {
          instructions, // Directly update the instructions as a single string
          resources: [...resources], // Update resources if needed
        },
      };
    },
    uploadResource: (state, action) => {
      const { category, file } = action.payload; // `file` is a File object from the input
      const categoryData = state.categories[category];

      if (categoryData) {
        const fileMetadata = {
          name: file.name,
          size: `${(file.size / 1024).toFixed(2)} KB`, // Convert size to KB
          type: file.type || "Unknown",
          uploadedAt: new Date(file.lastModified).toLocaleString(),
        };

        // Prevent duplicate files (based on name)
        if (
          !categoryData.resources.some((res) => res.name === fileMetadata.name)
        ) {
          categoryData.resources.push(fileMetadata);
        }
      }
    },

    deleteResource: (state, action) => {
      const { category, resource } = action.payload;
      const categoryData = state.categories[category];
      if (categoryData) {
        categoryData.resources = categoryData.resources.filter(
          (res) => res.name !== resource // Compare by resource name
        );
      }
    },
  },
});

export const {
  setDirectories,
  selectDirectory,
  addDirectory,
  addFile,
  uploadFile,
  editFile,
  deleteFile,
  deleteDirectory,
  setCategoryContent,
  uploadResource,
  deleteResource,
} = resourcesSlice.actions;

export default resourcesSlice.reducer;
